import { AxiosResponse } from "axios";
import { call, delay, put, select, takeLatest } from "redux-saga/effects";
import { toast } from "sonner";
import { retailerProductValidation } from "../../../../components/Common/Validations";
import CatchBlockFunction from "../../../../components/hooks/CatchBlockFunction";
import { get, patch, post, putType } from "../../../../utils/request";
import { IData } from "../types";
import {
  selectFormData,
  selectOrderBy,
  selectOrderDir,
  selectPageNo,
  selectPageSize,
  selectSearch,
  selectToggleLoading,
} from "./selector";
import { actions } from "./slice";

export function* doCreateRequest(action: { payload: { callback: any } }) {
  yield delay(500);

  const form: IData = yield select(selectFormData);
  const isValid = retailerProductValidation(form);
  if (!isValid) {
    return;
  }
  const data = {
    title: form?.title,
    price: Number(form?.price),
    taxRate: Number(form?.taxRate),
    sellingPrice: Number(form?.sellingPrice),
    images: form?.images,
    status: form?.status,
    categoryId: form?.categoryId,
    shipmentCapacity: Number(form?.shipmentCapacity),
    allergicImages: form?.allergicImages,
    stickers: form?.stickers,
    shortDescription: form?.shortDescription,
    longDescription: form?.longDescription,
    size: form?.size,
    code: form?.code,
    featuredImage: form?.featuredImage,
  };

  try {
    yield put(actions.setButtonLoading(true));
    const response: AxiosResponse = yield call(
      post,
      `admin/retailers/product/add`,
      data
    );
    yield put(actions.setButtonLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    toast.success(response.data.message);
    yield call(action?.payload?.callback());
  } catch (error: any) {
    yield put(actions.setButtonLoading(false));
    CatchBlockFunction(error);
  }
}
export function* doGetListRequest() {
  yield delay(500);

  try {
    const pageNo: number = yield select(selectPageNo);
    const pageSize: number = yield select(selectPageSize);
    const orderBy: string = yield select(selectOrderBy);
    const orderDir: string = yield select(selectOrderDir);
    const search: string = yield select(selectSearch);
    const buttonLoading: boolean = yield select(selectToggleLoading);
    if (!buttonLoading) {
      yield put(actions.setLoading(true));
    }

    const response: AxiosResponse = yield call(
      get,
      `admin/retailers/product/list?pageNo=${pageNo}&pageSize=${pageSize}${
        search.length > 0 ? "&search=" + search : ""
      }${orderBy.length > 0 ? "&orderBy=" + orderBy : ""}${
        orderDir.length > 0 ? "&orderDirection=" + orderDir : ""
      }`
    );
    yield put(actions.setLoading(false));
    yield put(actions.setToggleLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    yield put(
      actions.setTotalRow(
        response.data.data.meta ? response.data.data.meta.total : 0
      )
    );
    yield put(actions.setList(response.data.data.results));
  } catch (error: any) {
    yield put(actions.setLoading(false));
    yield put(actions.setToggleLoading(false));
    CatchBlockFunction(error);
  }
}
export function* doGetByIdRequest(action: { payload: string }) {
  yield delay(500);

  try {
    yield put(actions.setLoading(true));
    const response: AxiosResponse = yield call(
      get,
      `admin/retailers/product/get/${
        action.payload !== undefined && action.payload !== null
          ? action.payload
          : ""
      }`
    );
    yield put(actions.setLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    yield put(actions.setDataForm(response.data.data));
  } catch (error: any) {
    yield put(actions.setLoading(false));
    CatchBlockFunction(error);
  }
}
export function* doUpdateRequest(action: { payload: { callback: any } }) {
  yield delay(500);

  const form: IData = yield select(selectFormData);
  const isValid = retailerProductValidation(form);
  if (!isValid) {
    return;
  }
  const data = {
    title: form?.title,
    price: Number(form?.price),
    taxRate: Number(form?.taxRate),
    sellingPrice: Number(form?.sellingPrice),
    images: form?.images,
    status: form?.status,
    categoryId: form?.categoryId,
    shipmentCapacity: Number(form?.shipmentCapacity),
    allergicImages: form?.allergicImages,
    stickers: form?.stickers,
    shortDescription: form?.shortDescription,
    longDescription: form?.longDescription,
    size: form?.size,
    code: form?.code,
    featuredImage: form?.featuredImage,
  };

  try {
    yield put(actions.setButtonLoading(true));
    const response: AxiosResponse = yield call(
      putType,
      `admin/retailers/product/update/${form._id}`,
      data
    );
    yield put(actions.setButtonLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    toast.success(response.data.message);
    yield call(action?.payload?.callback());
  } catch (error: any) {
    yield put(actions.setButtonLoading(false));
    CatchBlockFunction(error);
  }
}

export function* doUpdateStatusRequest(action: {
  payload: { id: string; callback: () => void };
}) {
  yield delay(500);
  try {
    yield put(actions.setButtonLoading(true));
    yield put(actions.setToggleLoading(true));
    const response: AxiosResponse = yield call(
      patch,
      `admin/retailers/product/status/update/${
        action.payload.id !== undefined && action.payload.id !== null
          ? action.payload.id
          : ""
      }`,
      {}
    );
    yield put(actions.setButtonLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    toast.success(response.data.message);
    yield call(action.payload.callback);
  } catch (error: any) {
    yield put(actions.setButtonLoading(false));
    CatchBlockFunction(error);
  }
}
export function* doGetAllListRequest() {
  yield delay(500);

  try {
    const response: AxiosResponse = yield call(
      get,
      `admin/retailers/product/all`
    );
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    yield put(actions.setAllList(response.data.data));
  } catch (error: any) {
    CatchBlockFunction(error);
  }
}
export function* doGetExcelListRequest() {
  yield delay(500);

  try {
    const pageNo: number = yield select(selectPageNo);
    const pageSize: number = yield select(selectPageSize);
    const orderBy: string = yield select(selectOrderBy);
    const orderDir: string = yield select(selectOrderDir);
    const search: string = yield select(selectSearch);
    const response: AxiosResponse = yield call(
      get,
      `admin/retailer/product/excel/list?pageNo=${pageNo}&pageSize=${pageSize}${
        search.length > 0 ? "&search=" + search : ""
      }${orderBy.length > 0 ? "&orderBy=" + orderBy : ""}${
        orderDir.length > 0 ? "&orderDirection=" + orderDir : ""
      }`
    );
    yield put(actions.setLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    yield put(
      actions.setTotalRow(
        response.data.data.meta ? response.data.data.meta.total : 0
      )
    );
    yield put(actions.setExcelList(response.data.data.results));
  } catch (error: any) {
    yield put(actions.setLoading(false));

    CatchBlockFunction(error);
  }
}
export function* doGetExcelListByIdRequest(action: { payload: string }) {
  yield delay(500);

  try {
    yield put(actions.setLoading(true));
    const pageNo: number = yield select(selectPageNo);
    const pageSize: number = yield select(selectPageSize);
    const orderBy: string = yield select(selectOrderBy);
    const orderDir: string = yield select(selectOrderDir);
    const search: string = yield select(selectSearch);
    const response: AxiosResponse = yield call(
      get,
      `admin/retailer/product/file/content/${
        action.payload !== undefined && action.payload !== null
          ? action.payload
          : ""
      }?pageNo=${pageNo}&pageSize=${pageSize}${
        search.length > 0 ? "&search=" + search : ""
      }${orderBy.length > 0 ? "&orderBy=" + orderBy : ""}${
        orderDir.length > 0 ? "&orderDirection=" + orderDir : ""
      }`
    );
    yield put(actions.setLoading(false));
    if (response && !response.data) {
      toast.error(response.data.message);
      return;
    }
    yield put(
      actions.setTotalRow(
        response.data.data.meta ? response.data.data.meta.total : 0
      )
    );
    yield put(actions.setExcelDetailsList(response.data.data.results));
  } catch (error: any) {
    yield put(actions.setLoading(false));
    CatchBlockFunction(error);
  }
}

export function* uploadDocumentRequest(action: {
  payload: { fileName: string; name: string; callback: () => void };
}) {
  try {
    const response: AxiosResponse = yield call(
      post,
      `admin/retailer/product/upload-excel-file`,
      {
        fileName: action.payload.fileName,
        originalFileName: action.payload.name,
      }
    );
    if (!response.status) {
      toast.error(response.data.message);
    }
    yield call(action?.payload?.callback);
  } catch (error: any) {
    CatchBlockFunction(error);
  }
}

export function* productRetailerRepoSaga() {
  yield takeLatest(actions.doCreate, doCreateRequest);
  yield takeLatest(actions.doUpdate, doUpdateRequest);
  yield takeLatest(actions.doGetList, doGetListRequest);
  yield takeLatest(actions.doGetById, doGetByIdRequest);
  yield takeLatest(actions.doUpdateStatus, doUpdateStatusRequest);
  yield takeLatest(actions.doGetAllRetailerProductList, doGetAllListRequest);
  yield takeLatest(actions.doGetExcelList, doGetExcelListRequest);
  yield takeLatest(actions.doGetExcelListById, doGetExcelListByIdRequest);
  yield takeLatest(actions.uploadImage, uploadDocumentRequest);
}
